/**
 * Light/dark mode.
 *
 * `@hrms/ui`'s tokens.css already had a COMPLETE dark palette, driven automatically by
 * `prefers-color-scheme` and overridable via a `data-theme` attribute on `<html>` — see that
 * file's "Dark theme" comment. What was missing was anything that ever SET `data-theme`: there
 * was no manual override, only the OS preference. This is that missing piece, plus persistence.
 *
 * TWO MECHANISMS ARE DRIVEN TOGETHER, not one. `@hrms/ui`'s components read `data-theme`.
 * Signup.tsx is deliberately the one screen built on shadcn instead (see AuthBrand's doc
 * comment), and shadcn's generated CSS in styles.css uses Tailwind's `.dark` class convention
 * (`@custom-variant dark (&:is(.dark *))`) instead of an attribute. Toggling only one would leave
 * Signup out of sync with every other screen, so `applyTheme` always sets both.
 */

export type ThemePreference = 'light' | 'dark' | 'system';

const STORAGE_KEY = 'hrms.theme';

function isThemePreference(value: unknown): value is ThemePreference {
  return value === 'light' || value === 'dark' || value === 'system';
}

function systemPrefersDark(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-color-scheme: dark)').matches
  );
}

/** Wrapped: localStorage throws in some private-browsing modes — see token-store.ts's own guard. */
export function getStoredTheme(): ThemePreference {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return isThemePreference(stored) ? stored : 'system';
  } catch {
    return 'system';
  }
}

/**
 * Paints the preference onto the document. Exported separately from `setTheme` so the inline
 * bootstrap script in index.html — which cannot import this module — and this module's own
 * `setTheme` apply the identical logic rather than two copies drifting apart.
 */
export function applyTheme(preference: ThemePreference): void {
  const root = document.documentElement;
  const isDark = preference === 'dark' || (preference === 'system' && systemPrefersDark());

  // 'system' removes the override entirely, letting tokens.css's own `prefers-color-scheme`
  // media query decide — that block is guarded to defer to an explicit `data-theme`, so leaving
  // neither set is what "follow the OS" actually means, not `data-theme="system"`.
  if (preference === 'system') delete root.dataset.theme;
  else root.dataset.theme = preference;

  root.classList.toggle('dark', isDark);
}

export function setTheme(preference: ThemePreference): void {
  try {
    if (preference === 'system') localStorage.removeItem(STORAGE_KEY);
    else localStorage.setItem(STORAGE_KEY, preference);
  } catch {
    // Falls back to an in-memory-only choice for this tab — the theme still applies below, it
    // just won't survive a reload. Same trade-off token-store.ts already makes for the session.
  }
  applyTheme(preference);
}

/**
 * What's actually showing right now — 'system' resolved against the current OS preference.
 *
 * The UI only ever offers a light/dark CHOICE (see ThemeToggle), never 'system' as a
 * destination: 'system' is the untouched default, not a third mode to cycle through. This is
 * what lets that toggle show the right icon and flip to the right opposite on the very first
 * click, before anyone has made an explicit choice at all.
 */
export function resolveTheme(preference: ThemePreference): 'light' | 'dark' {
  return preference === 'dark' || (preference === 'system' && systemPrefersDark())
    ? 'dark'
    : 'light';
}

/**
 * Keeps the page in sync with OS-level changes while the preference is 'system' — someone
 * switching their OS to dark mode at 6pm should not need to reload this tab to see it. Repaints
 * the document unconditionally on every OS change (harmless once an explicit choice is stored:
 * `applyTheme` always reads the CURRENT stored preference, never assumes 'system'), and calls
 * `onChange` every time too, so a mounted ThemeToggle can keep its icon in sync independent of
 * that repaint.
 */
export function watchSystemTheme(onChange?: () => void): () => void {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return () => {};

  const query = window.matchMedia('(prefers-color-scheme: dark)');
  const handleChange = () => {
    applyTheme(getStoredTheme());
    onChange?.();
  };
  query.addEventListener('change', handleChange);
  return () => query.removeEventListener('change', handleChange);
}
